import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon, Check, ChevronLeft, ChevronRight, ArrowLeftRight } from "lucide-react";
import { DateRange, DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { DatePreset, DATE_PRESET_LABELS, getDateRangeFromPreset } from "@/utils/datePresets";
import { useTimezoneUtils } from "@/hooks/useTimezoneUtils";
import { useCompanyLocale } from "@/hooks/useCompanyLocale";

interface DateRangePickerProps {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  onDateChange: (from: Date | undefined, to: Date | undefined) => void;
  /** When provided, a Compare toggle appears inside the date picker */
  compareEnabled?: boolean;
  /** Called immediately when the user toggles compare (not gated by Apply) */
  onCompareChange?: (enabled: boolean) => void;
  className?: string;
  align?: "start" | "center" | "end";
}

const PRESETS: DatePreset[] = [
  'today',
  'yesterday',
  'this_week',
  'last_7_days',
  'last_week',
  'last_14_days',
  'this_month',
  'last_30_days',
  'last_month',
  'all',
];

/** Calculate the mirror-image previous period for display */
function calcPreviousPeriod(from: Date, to: Date) {
  const durationMs = to.getTime() - from.getTime();
  const prevTo = new Date(from.getTime() - 1);
  const prevFrom = new Date(prevTo.getTime() - durationMs);
  return { from: prevFrom, to: prevTo };
}

export function DateRangePicker({
  dateFrom,
  dateTo,
  onDateChange,
  compareEnabled,
  onCompareChange,
  className,
  align = "start",
}: DateRangePickerProps) {
  const { timezone, timezoneShortCode, formatDate, startOfDay, endOfDay, parseDate, formatDateInput } = useTimezoneUtils();
  const { locale } = useCompanyLocale();
  const [isOpen, setIsOpen] = React.useState(false);
  const [activePreset, setActivePreset] = React.useState<DatePreset | 'custom'>('today');
  
  // Internal state for pending changes (before Apply)
  const [pendingFrom, setPendingFrom] = React.useState<Date | undefined>(dateFrom);
  const [pendingTo, setPendingTo] = React.useState<Date | undefined>(dateTo);
  const [pendingPreset, setPendingPreset] = React.useState<DatePreset | 'custom'>('today');

  // Sync internal state when popover opens
  React.useEffect(() => {
    if (isOpen) {
      setPendingFrom(dateFrom);
      setPendingTo(dateTo);
      setPendingPreset(activePreset);
    }
  }, [isOpen]);

  // Comparison period calculated from pending dates (updates live as user picks)
  const comparisonDates = React.useMemo(() => {
    const from = pendingFrom;
    const to = pendingTo;
    if (!from || !to) return null;
    return calcPreviousPeriod(from, to);
  }, [pendingFrom, pendingTo]);

  const showCompare = onCompareChange !== undefined;
  const isCompareActive = compareEnabled && comparisonDates;

  const handlePresetClick = (preset: DatePreset) => {
    setPendingPreset(preset);
    const range = getDateRangeFromPreset(preset, timezone, locale.weekStartsOn);
    setPendingFrom(range.from);
    setPendingTo(range.to);
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    setPendingPreset('custom');
    if (range?.from) {
      const from = startOfDay(range.from);
      const to = range.to ? endOfDay(range.to) : endOfDay(range.from);
      setPendingFrom(from);
      setPendingTo(to);
    } else {
      setPendingFrom(undefined);
      setPendingTo(undefined);
    }
  };

  const handleFromInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      setPendingPreset('custom');
      setPendingFrom(parseDate(value));
    }
  };

  const handleToInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      setPendingPreset('custom');
      setPendingTo(endOfDay(parseDate(value)));
    }
  };

  const handleApply = () => {
    setActivePreset(pendingPreset);
    onDateChange(pendingFrom, pendingTo);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setPendingFrom(dateFrom);
    setPendingTo(dateTo);
    setPendingPreset(activePreset);
    setIsOpen(false);
  };

  const displayValue = React.useMemo(() => {
    if (!dateFrom && !dateTo) return "All Time";
    if (dateFrom && dateTo) {
      return `${formatDate(dateFrom, 'MMM d, yyyy')} - ${formatDate(dateTo, 'MMM d, yyyy')}`;
    }
    if (dateFrom) return `From ${formatDate(dateFrom, 'MMM d, yyyy')}`;
    return "Select dates";
  }, [dateFrom, dateTo, formatDate]);

  // Calendar modifiers for comparison range highlighting
  const compareModifiers = isCompareActive
    ? { compareRange: { from: comparisonDates.from, to: comparisonDates.to } }
    : {};

  const inputClassName = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

  return (
    <Popover open={isOpen} onOpenChange={(open) => {
      if (!open) {
        setPendingFrom(dateFrom);
        setPendingTo(dateTo);
        setPendingPreset(activePreset);
      }
      setIsOpen(open);
    }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal min-w-[260px]",
            !dateFrom && !dateTo && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <div className="flex flex-col">
          <div className="flex">
            {/* Left Panel - Presets & Compare */}
            <div className="border-r p-3 space-y-1 min-w-[160px] flex flex-col">
              <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Quick Select</p>
              {PRESETS.map((preset) => (
                <button
                  key={preset}
                  onClick={() => handlePresetClick(preset)}
                  className={cn(
                    "w-full text-left px-3 py-1.5 text-sm rounded-md flex items-center justify-between",
                    "hover:bg-accent hover:text-accent-foreground transition-colors",
                    pendingPreset === preset && "bg-accent text-accent-foreground font-medium"
                  )}
                >
                  <span>{DATE_PRESET_LABELS[preset]}</span>
                  {pendingPreset === preset && <Check className="h-4 w-4" />}
                </button>
              ))}

              {/* Compare toggle */}
              {showCompare && (
                <>
                  <div className="border-t my-2 !mt-3" />
                  <button
                    onClick={() => onCompareChange?.(!compareEnabled)}
                    className={cn(
                      "w-full text-left px-3 py-1.5 text-sm rounded-md flex items-center gap-2",
                      "hover:bg-accent hover:text-accent-foreground transition-colors",
                    )}
                  >
                    <Switch
                      checked={compareEnabled ?? false}
                      onCheckedChange={onCompareChange}
                      className="scale-75 origin-left"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="font-medium">Compare</span>
                  </button>
                  {isCompareActive && (
                    <div className="px-3 py-1 text-xs text-muted-foreground">
                      Previous period
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right Panel - Calendar & Date Inputs */}
            <div className="p-3">
              {/* Main Date Inputs */}
              <div className="flex gap-2 mb-2">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">Start date ({timezoneShortCode})</label>
                  <input
                    type="date"
                    value={pendingFrom ? formatDateInput(pendingFrom) : ''}
                    onChange={handleFromInputChange}
                    className={inputClassName}
                  />
                </div>
                <div className="flex items-end pb-2 text-muted-foreground">—</div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">End date ({timezoneShortCode})</label>
                  <input
                    type="date"
                    value={pendingTo ? formatDateInput(pendingTo) : ''}
                    onChange={handleToInputChange}
                    className={inputClassName}
                  />
                </div>
              </div>

              {/* Comparison Date Inputs (read-only) */}
              {isCompareActive && (
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <label className="text-xs text-success mb-1 block font-medium">Compare</label>
                    <input
                      type="date"
                      value={formatDateInput(comparisonDates.from)}
                      disabled
                      className={cn(inputClassName, "bg-success/10 border-success/20 text-muted-foreground cursor-default")}
                    />
                  </div>
                  <div className="flex items-end pb-2 text-muted-foreground">—</div>
                  <div className="flex-1">
                    <label className="text-xs text-success mb-1 block font-medium">&nbsp;</label>
                    <input
                      type="date"
                      value={formatDateInput(comparisonDates.to)}
                      disabled
                      className={cn(inputClassName, "bg-success/10 border-success/20 text-muted-foreground cursor-default")}
                    />
                  </div>
                </div>
              )}

              {/* Dual Month Calendar */}
              <DayPicker
                mode="range"
                selected={{ from: pendingFrom, to: pendingTo }}
                onSelect={handleRangeSelect}
                numberOfMonths={2}
                showOutsideDays
                className="pointer-events-auto"
                modifiers={compareModifiers}
                modifiersClassNames={{
                  compareRange: "[&:not([aria-selected])]:bg-success/15 [&:not([aria-selected])]:text-success",
                }}
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "flex justify-center pt-1 relative items-center",
                  caption_label: "text-sm font-medium",
                  nav: "space-x-1 flex items-center",
                  nav_button: cn(
                    buttonVariants({ variant: "outline" }),
                    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
                  ),
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: cn(
                    "h-9 w-9 text-center text-sm p-0 relative",
                    "[&:has([aria-selected].day-range-end)]:rounded-r-md",
                    "[&:has([aria-selected].day-outside)]:bg-accent/50",
                    "[&:has([aria-selected])]:bg-accent",
                    "first:[&:has([aria-selected])]:rounded-l-md",
                    "last:[&:has([aria-selected])]:rounded-r-md",
                    "focus-within:relative focus-within:z-20",
                  ),
                  day: cn(
                    buttonVariants({ variant: "ghost" }),
                    "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
                  ),
                  day_range_end: "day-range-end",
                  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                  day_today: "bg-accent text-accent-foreground",
                  day_outside: "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                  day_disabled: "text-muted-foreground opacity-50",
                  day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                  day_hidden: "invisible",
                }}
                components={{
                  IconLeft: () => <ChevronLeft className="h-4 w-4" />,
                  IconRight: () => <ChevronRight className="h-4 w-4" />,
                }}
              />
            </div>
          </div>
          
          {/* Footer with Cancel/Apply buttons */}
          <div className="flex justify-end gap-2 p-3 border-t bg-muted/30">
            <Button variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
